import React, { useState, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { sendApiRequest } from '../../utils';

const MonthWiseExcelReport = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [stats, setStats] = useState({
    totalAmount: 0,
    totalPayments: 0,
    averagePayment: 0
  });

  // Fetch data when year or month changes
  useEffect(() => {
    fetchMonthlyPayments();
  }, [year, month]);

  const fetchMonthlyPayments = async () => {
    setLoading(true);
    try {
      const response1 = await sendApiRequest(`api/packages/monthly-payments`, {year,month}, 'GET');
      console.log('response1', response1);

      setPayments(response1.data.data.payments || []);
      calculateStats(response1.data.data.payments || []);
      prepareCSVData(response1.data.data.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
      setCsvData([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const totalAmount = data.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const totalPayments = data.length;
    const averagePayment = totalPayments > 0 ? totalAmount / totalPayments : 0;

    setStats({
      totalAmount,
      totalPayments,
      averagePayment: Math.round(averagePayment)
    });
  };

  const prepareCSVData = (data) => {
    // Prepare headers for CSV
    const headers = [
      { label: 'Date', key: 'date' },
      { label: 'Receipt Number', key: 'receiptNumber' },
      { label: 'Customer Name', key: 'customerName' },
      { label: 'Customer Phone', key: 'customerPhone' },
    //   { label: 'Customer Email', key: 'customerEmail' },
      { label: 'Package Title', key: 'packageTitle' },
      { label: 'Package Price (₹)', key: 'packagePrice' },
      { label: 'Current Month Amount Paid (₹)', key: 'amount' },
      { label: 'Total Amount (₹)', key: 'totalAmount' },
      { label: 'Remaining Amount (₹)', key: 'remainingAmount' },
      { label: 'Payment Mode', key: 'paymentMode' },
    //   { label: 'UTR/Reference', key: 'paymentDetails' },
      { label: 'Partner Name', key: 'partnerName' },
      { label: 'Sponsor Code', key: 'sponsorCode' },
    //   { label: 'Partner Email', key: 'partnerEmail' },
    //   { label: 'Amount in Words', key: 'amountInWords' },
    //   { label: 'Remarks', key: 'remarks' },
    //   { label: 'Description', key: 'description' }
    ];

    // Transform data for CSV
    const csvData = data.map(payment => ({
      date: new Date(payment.date).toLocaleDateString('en-IN'),
      receiptNumber: payment.receiptNumber || 'N/A',
      customerName: payment.customer?.customerName || 'N/A',
      customerPhone: payment.customer?.customerPhone || 'N/A',
      customerEmail: payment.customer?.customerEmail || 'N/A',
      packageTitle: payment.package?.title || 'N/A',
      packagePrice: payment.package?.price || 0,
      amount: payment.amount || 0,
      totalAmount: payment.totalAmount || 0,
      remainingAmount: payment.remainingAmount || 0,
      paymentMode: payment.paymentMode || 'N/A',
      paymentDetails: payment.paymentDetails?.replace('UTR : ', '') || 'N/A',
      partnerName: payment.partner?.fullName || 'N/A',
      sponsorCode: payment.partner?.sponsorCode || 'N/A',
      partnerEmail: payment.partner?.email || 'N/A',
      amountInWords: payment.amountInWords || 'N/A',
      remarks: payment.remarks || 'N/A',
      description: payment.description || 'N/A'
    }));

    // Add summary row
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const summaryRow = {
      date: 'SUMMARY',
      receiptNumber: '',
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      packageTitle: '',
      packagePrice: `Total Payments: ${stats.totalPayments}`,
      amount: `Total Amount: ₹${stats.totalAmount}`,
      totalAmount: `Average Payment: ₹${stats.averagePayment}`,
      remainingAmount: '',
      paymentMode: '',
      paymentDetails: '',
      partnerName: '',
      sponsorCode: '',
      partnerEmail: '',
      amountInWords: '',
      remarks: `Month: ${monthNames[month - 1]} ${year}`,
      description: ''
    };

    setCsvData({
      data: [...csvData, summaryRow],
      headers: headers,
      filename: `Monthly_Payments_${monthNames[month - 1]}_${year}.csv`
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPaymentModeColor = (mode) => {
    const colors = {
      upi: 'bg-green-100 text-green-800',
      cash: 'bg-blue-100 text-blue-800',
      card: 'bg-purple-100 text-purple-800',
      cheque: 'bg-yellow-100 text-yellow-800',
      bank_transfer: 'bg-indigo-100 text-indigo-800',
      online: 'bg-pink-100 text-pink-800'
    };
    return colors[mode] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Monthly Payments Report</h1>
          <p className="text-gray-600 mt-2 text-sm md:text-base">View and export monthly payment transactions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-xl font-semibold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Payments</p>
                <p className="text-xl font-semibold text-gray-900">{stats.totalPayments}</p>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Average Payment</p>
                <p className="text-xl font-semibold text-gray-900">{formatCurrency(stats.averagePayment)}</p>
              </div>
            </div>
          </div> */}
        </div>

        {/* Filters and Controls */}
        <div className="bg-white rounded-xl shadow-md p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="w-full md:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  className="w-full md:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={fetchMonthlyPayments}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading...
                  </>
                ) : (
                  'Refresh Data'
                )}
              </button>

              {csvData.data && csvData.data.length > 0 && (
                <CSVLink
                  {...csvData}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export to Excel
                </CSVLink>
              )}
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No payments found</h3>
              <p className="mt-1 text-sm text-gray-500">No payment records for {new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })} {year}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.receiptNumber}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{payment.customer?.customerName}</div>
                        <div className="text-sm text-gray-500">{payment.customer?.customerPhone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{payment.package?.title}</div>
                        <div className="text-sm text-gray-500">Total: {formatCurrency(payment.totalAmount)}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">{formatCurrency(payment.amount)}</div>
                        <div className="text-xs text-gray-500">Remaining: {formatCurrency(payment.remainingAmount)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentModeColor(payment.paymentMode)}`}>
                            {payment.paymentMode?.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[150px]">
                          {payment.paymentDetails}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{payment.partner?.fullName}</div>
                        <div className="text-xs text-gray-500">{payment.partner?.sponsorCode}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Paid
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthWiseExcelReport;